import { useQuery } from '@tanstack/react-query';
import { getTenantTheme } from '../services/tenant-theme';
import { getThemeCookie, setThemeCookie } from '../services';
import type { FullTheme } from '../utils/theming';
import { applyTenantMetadata } from '@/utils/tenant-metadata';

type TTenantTheme = {
  theme: {
    primaryColor: string | null;
    secondaryColor: string | null;
    logoUrl: string | null;
    faviconUrl: string | null;
    themeConfig: Record<string, any> | null;
    metadata?: {
      title?: string | null;
      description?: string | null;
    } | null;
    name: string | null;
    title?: string | null;
    description?: string | null;
    paymentMethod?: 'prepaid' | 'postpaid' | null;
    currency?: string | null;
  };
};

interface TenantThemeResponse {
  success: boolean;
  data?: TTenantTheme;
  theme?: TTenantTheme['theme'];
}

interface UseTenantThemeResult {
  theme: TTenantTheme['theme'] | null;
  tenant: {
    name: string | null;
    logoUrl: string | null;
    title: string | null;
    description: string | null;
    faviconUrl: string | null;
    paymentMethod: 'prepaid' | 'postpaid' | null;
    currency: string | null;
  } | null;
  computedTheme: FullTheme | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

type TenantThemeQueryData = {
  theme: TTenantTheme['theme'] | null;
  tenant: {
    name: string | null;
    logoUrl: string | null;
    title: string | null;
    description: string | null;
    faviconUrl: string | null;
    paymentMethod: 'prepaid' | 'postpaid' | null;
    currency: string | null;
  } | null;
  computedTheme: FullTheme | null;
};

const BASE_ALLOWED_ORIGINS: (string | RegExp)[] = [
  'http://dev-smb.path-iq.ai',
  'https://dev-smbapi.path-iq.ai',
  'https://dev-smb.path-iq.ai',
  /\.path-iq\.ai$/,
];

const DEV_ALLOWED_ORIGINS: RegExp[] = [
  /^http:\/\/[\w-]+\.localhost(:\d+)?$/,
  /^http:\/\/[\w-]+\.127\.0\.0\.1(:\d+)?$/,
];

const getAllowedOrigins = (): (string | RegExp)[] =>
  process.env.NODE_ENV !== 'production'
    ? [...BASE_ALLOWED_ORIGINS, ...DEV_ALLOWED_ORIGINS]
    : BASE_ALLOWED_ORIGINS;

const isTenantSubdomain = (hostname: string): boolean => {
  if (hostname === 'localhost' || hostname === '127.0.0.1') return false;
  if (hostname.endsWith('.localhost') || hostname.endsWith('.127.0.0.1')) {
    return true;
  }

  return hostname.split('.').length > 2;
};

const isSubdomainRoute = (): boolean => {
  if (typeof window === 'undefined') return false;

  const origin = window.location.origin.toLowerCase();
  const hostname = window.location.hostname.toLowerCase();

  const isAllowedOrigin = getAllowedOrigins().some((allowed) =>
    typeof allowed === 'string'
      ? allowed.toLowerCase() === origin
      : allowed.test(origin) || allowed.test(hostname)
  );

  return isAllowedOrigin && isTenantSubdomain(hostname);
};

const getInitialThemeData = (
  shouldFetchTenantTheme: boolean
): TenantThemeQueryData | undefined => {
  if (!shouldFetchTenantTheme) return undefined;

  const cachedTheme = getThemeCookie();
  if (!cachedTheme) return undefined;

  const normalizedTheme: TTenantTheme['theme'] = {
    ...cachedTheme.colors,
    name: cachedTheme.colors.name ?? null,
    logoUrl: cachedTheme.colors.logoUrl ?? null,
    title: cachedTheme.colors.title ?? null,
    description: cachedTheme.colors.description ?? null,
    faviconUrl: cachedTheme.colors.faviconUrl ?? null,
    paymentMethod: cachedTheme.colors.paymentMethod ?? null,
    currency: cachedTheme.colors.currency ?? null,
  };

  applyTenantMetadata(normalizedTheme);

  return {
    theme: normalizedTheme,
    tenant: {
      name: normalizedTheme.name,
      logoUrl: normalizedTheme.logoUrl,
      title: normalizedTheme.title ?? null,
      description: normalizedTheme.description ?? null,
      faviconUrl: normalizedTheme.faviconUrl ?? null,
      paymentMethod: normalizedTheme.paymentMethod ?? null,
      currency: normalizedTheme.currency ?? null,
    },
    computedTheme: cachedTheme.computed,
  };
};

const fetchTenantTheme = async (): Promise<TenantThemeQueryData> => {
  const response: TenantThemeResponse | null = await getTenantTheme();

  if (response?.success && (response?.data?.theme || response?.theme)) {
    const newTheme = response.data?.theme || response.theme!;
    const themeTitle = newTheme.metadata?.title ?? newTheme.title ?? null;
    const themeDescription =
      newTheme.metadata?.description ?? newTheme.description ?? null;
    const normalizedTheme: TTenantTheme['theme'] = {
      ...newTheme,
      title: themeTitle,
      description: themeDescription,
    };
    setThemeCookie(normalizedTheme);
    const cachedTheme = getThemeCookie();
    applyTenantMetadata({
      name: newTheme.name ?? cachedTheme?.colors.name ?? null,
      title: themeTitle ?? cachedTheme?.colors.title ?? null,
      description: themeDescription ?? cachedTheme?.colors.description ?? null,
      faviconUrl: newTheme.faviconUrl ?? cachedTheme?.colors.faviconUrl ?? null,
      logoUrl: newTheme.logoUrl ?? cachedTheme?.colors.logoUrl ?? null,
    });

    return {
      theme: normalizedTheme,
      tenant: {
        name: normalizedTheme.name ?? cachedTheme?.colors.name ?? null,
        logoUrl: normalizedTheme.logoUrl ?? cachedTheme?.colors.logoUrl ?? null,
        title: themeTitle ?? cachedTheme?.colors.title ?? null,
        description:
          themeDescription ?? cachedTheme?.colors.description ?? null,
        faviconUrl:
          normalizedTheme.faviconUrl ?? cachedTheme?.colors.faviconUrl ?? null,
        paymentMethod:
          normalizedTheme.paymentMethod ??
          cachedTheme?.colors.paymentMethod ??
          null,
        currency:
          normalizedTheme.currency ?? cachedTheme?.colors.currency ?? null,
      },
      computedTheme: cachedTheme?.computed ?? null,
    };
  }

  return {
    theme: null,
    tenant: null,
    computedTheme: null,
  };
};

export const useTenantTheme = (): UseTenantThemeResult => {
  const shouldFetchTenantTheme = isSubdomainRoute();

  const { data, isPending, error, refetch } = useQuery<
    TenantThemeQueryData,
    Error
  >({
    queryKey: ['tenant-theme'],
    queryFn: fetchTenantTheme,
    enabled: shouldFetchTenantTheme,
    initialData: () => getInitialThemeData(shouldFetchTenantTheme),
    staleTime: 5 * 60 * 1000,
  });

  return {
    theme: data?.theme ?? null,
    tenant: data?.tenant ?? null,
    computedTheme: data?.computedTheme ?? null,
    loading: isPending,
    error,
    refetch: async () => {
      if (!shouldFetchTenantTheme) return;
      await refetch();
    },
  };
};
